import React from "react";
import { Card } from "@/ds";
import { cockpit } from "@/api";
import { cachedRead } from "@/api/read-cache";
import { useT } from "../i18n";
import { AddressWithCopy, WorkingAddressInstructions } from "./working-address";

/**
 * The address this business receives mail at (#779), and what to do with it (#1229).
 *
 * A statement, not a setting. A hosted address was chosen once while the business registered; it
 * is on their invoices and in their customers' address books, so changing it silently stops that
 * mail arriving. Correcting one is an owner-plane exception, which is why this card has no edit
 * affordance at all rather than a disabled one — a greyed-out button invites a support request,
 * while a plain sentence answers the question the person actually came here with, which is
 * "what is our address".
 *
 * Copying is the one thing it does offer, because the address exists to be pasted somewhere else:
 * onto a website, into a signature, onto an invoice.
 *
 * Since #1229 this also renders for a business that brought its own mailbox. It used to render
 * nothing for them — the server answered null — which left the businesses least likely to know
 * how the address is meant to be used with nothing at all. The wording differs where the facts
 * differ; the instruction does not.
 *
 * Renders nothing when no live address is configured, which is a business that cannot receive
 * anything yet: an empty card explaining an address they do not have would be noise.
 */
export function BusinessAddressCard() {
  const t = useT();
  // #1536: the address from the last visit stays on screen while it refreshes.
  const [address, setAddress] = React.useState<string | null>(() => cachedRead("tenantNeoMailbox")?.emailAddress ?? null);
  const [hosted, setHosted] = React.useState(() => cachedRead("tenantNeoMailbox")?.hosted ?? false);

  React.useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const view = await cockpit.tenantNeoMailbox();
        if (!live) return;
        setAddress(view.emailAddress);
        setHosted(view.hosted);
      } catch {
        // A failed read shows nothing rather than an error: this is informational, and a
        // business could not act on the failure either way. That includes an address remembered
        // from an earlier visit (#1536).
        if (live) setAddress(null);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  if (!address) return null;

  return (
    <Card title={t("businessAddress.title")} data-testid="business-address">
      <AddressWithCopy address={address} />
      <p className="subtle management-copy" style={{ margin: "var(--space-2) 0 0", fontSize: "var(--text-sm)" }}>
        {hosted ? t("businessAddress.hint") : t("businessAddress.hintOwn")}
      </p>
      <WorkingAddressInstructions hosted={hosted} />
    </Card>
  );
}
